// The pure side of the video script generator: how long a script takes to say (the same rule as the server's), the files a script can be saved as, the scripts the visitor
// keeps in this browser, and the hand-over of a script to the other video tools. No drawing here.
import { subtitleChunks } from "./timeline";

export const WORDS_PER_SECOND = 2.5;       // a presenter says about 150 words a minute
export const PAUSE_SECONDS = 0.5;          // a breath at the end of every scene
export const TONE_PACE = { friendly: 1, professional: 0.95, energetic: 1.1, calm: 0.88, humorous: 1.05, inspiring: 0.95, urgent: 1.12 };

export const countWords = (text) => (String(text || "").match(/\S+/g) || []).length;

// How long a narration takes to say, with the breath after it (server: scriptgen.narration_seconds).
export function narrationSeconds(narration, tone = "friendly") {
  const words = countWords(narration);
  if (!words) return 2;
  return Math.round(Math.max(2, words / (WORDS_PER_SECOND * (TONE_PACE[tone] || 1)) + PAUSE_SECONDS) * 10) / 10;
}

export const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
export function clockWords(seconds) {
  const s = Math.round(seconds);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;
}

// Where each scene starts and ends: [{ start, end, seconds }] and the total.
export function timings(scenes, tone) {
  let at = 0;
  const parts = scenes.map((scene) => {
    const seconds = narrationSeconds(scene.narration, tone);
    const part = { start: at, end: at + seconds, seconds };
    at += seconds;
    return part;
  });
  return { parts, total: at };
}

// Whether the script is the length that was asked for: "good" within a fifth either way.
export function fit(total, target) {
  const ratio = target > 0 ? total / target : 1;
  return { state: ratio > 1.2 ? "long" : ratio < 0.8 ? "short" : "good", ratio };
}

// ------------------------------------------------------------------------------------------ files
export const toNarration = (script) => script.scenes.map((s) => s.narration.trim()).filter(Boolean).join("\n\n");

export function toText(script) {
  const { parts } = timings(script.scenes, script.tone);
  const lines = [script.title.toUpperCase(), script.summary || "", ""];
  script.scenes.forEach((s, i) => {
    lines.push(`SCENE ${i + 1} - ${s.label} (${clock(parts[i].start)}-${clock(parts[i].end)})`);
    lines.push(`VOICE:     ${s.narration}`);
    if (s.on_screen) lines.push(`ON SCREEN: ${s.on_screen}`);
    if (s.visual) lines.push(`VISUAL:    ${s.visual}`);
    if (s.sound) lines.push(`SOUND:     ${s.sound}`);
    lines.push("");
  });
  if (script.titles?.length) lines.push("TITLES", ...script.titles.map((t) => `- ${t}`), "");
  if (script.description) lines.push("DESCRIPTION", script.description, "");
  if (script.hashtags?.length) lines.push("HASHTAGS", script.hashtags.join(" "), "");
  if (script.thumbnail) lines.push("THUMBNAIL", script.thumbnail, "");
  return `${lines.join("\n").trim()}\n`;
}

export function toMarkdown(script) {
  const { parts, total } = timings(script.scenes, script.tone);
  const lines = [`# ${script.title}`, "", script.summary ? `_${script.summary}_` : "", "", `**Length:** about ${clockWords(total)} · ${script.scenes.length} scenes · ${countWords(toNarration(script))} words`, ""];
  script.scenes.forEach((s, i) => {
    lines.push(`## ${i + 1}. ${s.label} (${clock(parts[i].start)}-${clock(parts[i].end)})`, "", `**Voice:** ${s.narration}`, "");
    if (s.on_screen) lines.push(`**On screen:** ${s.on_screen}`, "");
    if (s.visual) lines.push(`**Visual:** ${s.visual}`, "");
    if (s.sound) lines.push(`**Sound:** ${s.sound}`, "");
  });
  if (script.titles?.length) lines.push("## Titles", "", ...script.titles.map((t) => `- ${t}`), "");
  if (script.description) lines.push("## Description", "", script.description, "");
  if (script.hashtags?.length) lines.push("## Hashtags", "", script.hashtags.join(" "), "");
  if (script.thumbnail) lines.push("## Thumbnail idea", "", script.thumbnail, "");
  return `${lines.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
}

const srtTime = (seconds) => {
  const ms = Math.round(Math.max(0, seconds) * 1000);
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${pad(Math.floor(ms / 3600000))}:${pad(Math.floor((ms % 3600000) / 60000))}:${pad(Math.floor((ms % 60000) / 1000))},${pad(ms % 1000, 3)}`;
};

// Subtitles (.srt) for an editing program: every scene's narration cut into short lines, spread over the time it takes to say it.
export function toSrt(script) {
  const { parts } = timings(script.scenes, script.tone);
  const entries = [];
  script.scenes.forEach((scene, i) => {
    const spoken = Math.max(0.5, parts[i].seconds - PAUSE_SECONDS);
    subtitleChunks(scene.narration, spoken, 7).forEach((chunk) => entries.push({ from: parts[i].start + chunk.from, to: parts[i].start + chunk.to, text: chunk.text }));
  });
  return entries.map((e, n) => `${n + 1}\n${srtTime(e.from)} --> ${srtTime(e.to)}\n${e.text}\n`).join("\n");
}

export const fileName = (title, extension) => `${String(title || "script").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50) || "script"}.${extension}`;

// ------------------------------------------------------------------------------------------ the scripts kept in this browser
const STORE = "video-scripts-v1";
export const MAX_KEPT = 12;
export const newId = () => `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function loadScripts() {
  try {
    const list = JSON.parse(localStorage.getItem(STORE) || "[]");
    return Array.isArray(list) ? list.filter((s) => s && s.id && Array.isArray(s.scenes)) : [];
  } catch {
    return [];
  }
}

// Saves (or updates) a script; the newest is first and only the newest MAX_KEPT are kept. Returns the list now kept.
export function saveScript(script) {
  const rest = loadScripts().filter((s) => s.id !== script.id);
  const list = [{ ...script, savedAt: Date.now() }, ...rest].slice(0, MAX_KEPT);
  try { localStorage.setItem(STORE, JSON.stringify(list)); } catch { /* storage full or blocked: the script is simply not kept */ }
  return list;
}

export function deleteScript(id) {
  const list = loadScripts().filter((s) => s.id !== id);
  try { localStorage.setItem(STORE, JSON.stringify(list)); } catch { /* ignore */ }
  return list;
}

// ------------------------------------------------------------------------------------------ handing a script to another video tool
const HANDOFF = "video-script-handoff";
export const TOOLS = {
  avatar: { label: "Presenter / avatar video", url: "/ai-video-generation?tool=ai-avatar-and-talking-head-videos", max: 3500, hint: "A presenter speaks the narration" },
  images: { label: "Video from my pictures", url: "/ai-video-generation?tool=image-to-video-generation", max: 2500, hint: "The AI shares the script among your photos" },
  text: { label: "Cinematic video from text", url: "/ai-video-generation?tool=text-to-video-generation", max: 2500, hint: "Pictures and narration for every scene" },
};

// The text, shortened at a sentence if it is longer than the tool takes: { text, cut }.
export function fitTo(text, max) {
  if (text.length <= max) return { text, cut: false };
  const head = text.slice(0, max);
  const end = Math.max(head.lastIndexOf(". "), head.lastIndexOf("! "), head.lastIndexOf("? "), head.lastIndexOf("\n"));
  return { text: (end > max * 0.5 ? head.slice(0, end + 1) : head).trim(), cut: true };
}

export function sendToTool(tool, script) {
  const { text, cut } = fitTo(toNarration(script).replace(/\n{2,}/g, "\n"), TOOLS[tool].max);
  try { sessionStorage.setItem(HANDOFF, JSON.stringify({ tool, text, title: script.title, at: Date.now() })); } catch { /* the tool simply opens empty */ }
  return { url: TOOLS[tool].url, cut };
}

// What another tool was sent (once): { text, title } for that tool, or null.
export function takeHandoff(tool) {
  try {
    const data = JSON.parse(sessionStorage.getItem(HANDOFF) || "null");
    if (!data || data.tool !== tool || Date.now() - data.at > 10 * 60 * 1000) return null;
    sessionStorage.removeItem(HANDOFF);
    return { text: data.text, title: data.title };
  } catch {
    return null;
  }
}
